import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Location, isPlatformServer } from '@angular/common';

import { User, UserAuthToken } from '../models';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { ApiConfiguration, UsersApi, ApiUser, ApiUserSnippet } from '../api';
import { Logger, Shell, Cookies, RequestParams, AppConfig, LoggerService } from '../common';
import { LocalStorageService } from 'angular-2-local-storage';
import jwtDecode from 'jwt-decode';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import { Angulartics2 } from 'angulartics2';
import { BantaService } from '@banta/sdk';

import Bugsnag from '@bugsnag/js';
import { environment } from '@tytapp/environment';

const USER_KEY = makeStateKey('tyt-user');

declare var gtag;

@Injectable()
export class UserService {
    constructor(
        private router : Router,
        private apiConfig : ApiConfiguration,
        private localStorage : LocalStorageService,
        private shell : Shell,
        private location : Location,
        private cookies : Cookies,
        private requestParams : RequestParams,
        private appConfig : AppConfig,
        private usersApi : UsersApi,
        private stateTransfer : TransferState,
        private logger : LoggerService,
        private angulartics2 : Angulartics2,
        private angulartics2GoogleTagManager : Angulartics2GoogleTagManager,
        private banta : BantaService,

        @Inject(PLATFORM_ID)
        private platformId : any
    ) {
        this.readyPromise = new Promise((res, rej) => this.markReady = res);
    }

    private _onboardingChanged = new BehaviorSubject<boolean>(false);
    private _onboarding = false;
    
    get onboardingChanged() {
        return this._onboardingChanged;
    }

    get onboarding() {
        return this._onboarding;
    }

    userToSnippet(user : ApiUser): ApiUserSnippet {
        return <ApiUserSnippet>{
            type: user.type,
            id: user.id,
            uuid: user.uuid,
            display_name: user.display_name,
            username: user.username,
            admin: user.admin,
            super_admin: user.super_admin,
            affiliated: user.affiliate ? true : false,
            staff: user.staff,
            avatar: user.profile ? user.profile.avatar : null
        };
    }
    
    async refresh() {
        let user = await this.usersApi.getCurrentUser().toPromise();
        this.user = <User>user;
    }

    validateEmail(email : string) {
        if (!email) 
            return 'Please enter the email address you would like to use.';
        
        if (email.endsWith('.con'))
            return 'The email you provided ends with ".con". You probably meant ".com"';
    
        if (email.endsWith('.comm'))
            return 'The email you provided ends with ".comm". You probably meant ".com"';
    
        if (email.endsWith('.ccom')) 
            return 'The email you provided ends with ".ccom". You probably meant ".com"';
    
        if (email.includes('@gmail') && !email.endsWith('.com'))
            return 'Looks like you have a GMail address but you have specified the wrong domain name. Check the email address you\'ve entered for errors. ';
        
        return null;
    }

    set onboarding(value) {
        if (value === this._onboarding)
            return;
        
        this._onboarding = value;
        this._onboardingChanged.next(value);
        this.localStorage.set('onboarding', value);
    }

    private _user : User;

    public firstTime : boolean = true;

    userChanged : BehaviorSubject<User> = new BehaviorSubject<User>(null);
    sourceChanged : BehaviorSubject<string> = new BehaviorSubject<string>(null);
    markReady : Function;
    readyPromise : Promise<void>;
    private _bounceUrl : string;

    get bounceURL() {
        if (typeof window !== 'undefined') {
            let storedBounceUrl = localStorage['tyt.userService.bounceUrl'];
            if (storedBounceUrl)
                this._bounceUrl = storedBounceUrl;
        }
        return this._bounceUrl;
    }

    set bounceURL(value) {
        this._bounceUrl = value;
        if (typeof window !== 'undefined') {
            if (value)
                localStorage['tyt.userService.bounceUrl'] = value;
            else
                delete localStorage['tyt.userService.bounceUrl'];
        }

        console.log(`UserService: Return URL is ${value}`);
    }

    get identityKey() {
        if (this.user)
            return `user_${this.user.id}`;

        return `anonymous`;
    }

    get ready() {
        return this.readyPromise;
    }

    get token(): string {
        return <string>this.apiConfig.accessToken;
    }

    get sourceFirstTime(): boolean {
        return <boolean>this.localStorage.get('trafficSourceFirstTime');
    }

    private _sourceStamp : Date;

    get sourceStamp(): Date {
        if (!this._sourceStamp) {
            this._sourceStamp = new Date(this.localStorage.get('trafficSourceStamp'));
        }

        return this._sourceStamp;
    }

    decodeToken(token): any {
        return jwtDecode(token);
    }

    async refreshToken() {

        if (!this._user || !this.apiConfig.accessToken)
            return;

        let tokenStr = this.apiConfig.accessToken
        let token = this.decodeToken(tokenStr);

        // Determine the appropriate renewal period before expiry

        let threshold = 60*60*8;

        if (token.renewalTime)
            threshold = token.renewalTime;
        else if (token.lifetime)
            threshold = Math.min(threshold, Math.round(token.lifetime * 0.25));
        
        let renewalTime = token.exp - threshold;

        if ((new Date().getTime() / 1000) < renewalTime)
            return;

        this.logger.info("Refreshing authentication...");
        let userAuthToken = <UserAuthToken> await this.usersApi.refreshAuthentication(token.rememberMe || false).toPromise();
        token = this.decodeToken(userAuthToken.token);
        let timeMs = 1000*(token.exp - token.renewalTime);
        
        this.logger.info(`Authentication refreshed. Renewal will happen at ${new Date(timeMs)}`);
        this.saveAuthentication(userAuthToken.user, userAuthToken.token);
    }

    async refreshTokenPeriodically() {

        await this.refreshToken();

        // Do not wait to refresh token for server-side rendering.
        // (Infinite loads)

        if (!isPlatformServer(this.platformId)) {
            setTimeout(() => {
                this.refreshTokenPeriodically();
            }, this._tokenRefreshInterval)
        }
    }

    private _tokenRefreshInterval : number = 1000 * 60 * 30;
    private _postLoginUrl : string = null;
    private _postLoginAction : Function = null;
    private _source : string = null;

    set postLoginUrl(value : string) {
        if (value != null)
            value = value.replace(/^\/+/, '');

        this._postLoginUrl = value;
        this._postLoginAction = null;
    }

    get postLoginUrl() {
        return this._postLoginUrl;
    }

    requireLogin() {
        if (!this.user) {
            this.router.navigate(['login']);
            return false;
        }

        return true;
    }

    /**
     * Do not navigate after login is complete. Used for inline signups.
     */
    stayAfterLogin() {
        this.afterLogin(() => {});
    }

    /**
     * Run the given function after login (and do not otherwise navigate)
     * @param action 
     */
    afterLogin(action : Function) {
        this._postLoginAction = action;
        this._postLoginUrl = null;
    }

    private _initialized = false;

    init() {
        if (this._initialized)
            return;
        this._initialized = true;

        if (this.localStorage.get('onboarding')) {
            this.onboarding = true;
        }

        let repeatVisitorCookie = this.cookies.get('repeatVisitor');
        if (repeatVisitorCookie) {
            this.firstTime = false;
        } else {
            this.firstTime = true;
            this.localStorage.set('repeatVisitor', "1");
            this.cookies.set('repeatVisitor', 1, 1000*60*60*24*365*5);
        }

        // TODO: swap out with equivalent Location service (this.location)
        let token : string = null;

        if (typeof location !== 'undefined') {
            let args = this.requestParams.params;
            if (args.token) {
                this.logger.log(`Storing passed token ${args.token}`);
                this.cookies.set(environment.auth.cookie, { token: args.token });

                // Remove the token from the current URL to avoid 
                // leaking tokens if the URL is shared 

                let newUrl = location.href.replace(/token=[^&]+/, 'tx=');
                history.replaceState({}, document.title, newUrl);
            }

            if (args.s) {
                let newSource = args.s;
                let currentSource = this.source;
                if (newSource != currentSource) {
                    if (this.source)
                        this.logger.info(`Traffic source changed from ${currentSource} to ${newSource}`);
                    else 
                        this.logger.info(`Traffic source: ${newSource}`);

                    this.source = newSource;
                }
            }
        }

        let authCookie = this.cookies.get<any>(environment.auth.cookie);
        let authCookieEnabled = true;
        
        if (typeof window !== 'undefined' && window['disableCookieAuthentication'])
            authCookieEnabled = false;

        if (authCookieEnabled && authCookie && !token)
            token = authCookie.token;

        // WHY WE DO NOT USE LOCALSTORAGE FOR THE AUTH TOKEN:
        // It is far too easy for the cookie and the localStorage to disagree. 
        // We should just rely on the cookie as the source of truth as it can 
        // be read by both the client side and server side apps

        // Install the token
        //this.logger.info(`Setting accessToken: '${token}'`);

        this.apiConfig.accessToken = token;

        if (authCookieEnabled && this.apiConfig.accessToken)
            this.cookies.set(environment.auth.cookie, { token: this.apiConfig.accessToken });

        if (token) {

            let user : User = this.stateTransfer.get<User>(USER_KEY, null);
            let wasTransferred : boolean = false;
            
            if (user) {
                user = new User(user);
                wasTransferred = true;
                if (!isPlatformServer(this.platformId))
                    Logger.inspect(`Preloaded user [SSR/ST]`, user);
            } else {
                wasTransferred = false;
                if (this.localStorage.get('user')) {
                    user = new User(this.localStorage.get('user'));
                    if (!isPlatformServer(this.platformId))
                        Logger.inspect(`Preloaded user [LS]`, user);
                }
            }

            this.user = user;

            if (wasTransferred) {
                this.localStorage.set('user', user);
                this.cookies.set(environment.auth.userCookie, user.uuid, null, true);
                
                this.markReady();
                this.refreshTokenPeriodically();
                return;
            }

            // Fetch the user 
            if (!wasTransferred) this.usersApi.getCurrentUser().toPromise()
                .then(rawUser => {
                    let user = <User>rawUser;
                    if (user && this.user)
                        user.assumed = this.user.assumed;

                    if (user.disabled) {
                        this.apiConfig.accessToken = null;
                
                        this.localStorage.remove('user');
                        this.cookies.remove(environment.auth.cookie);
                        this.cookies.remove(environment.auth.userCookie);

                        this.user = null;
                        this.markReady();
                        return;
                    }

                    if (!this.justLoggedOut) {
                        this.localStorage.set('user', user);
                        this.cookies.set(environment.auth.userCookie, user.uuid, null, true);
                        
                        this.user = user
                        this.markReady();

                        this.refreshTokenPeriodically();
                    }
                })
                .catch(e => {
                    if (e.json)
                        e = e.json();
                    
                    Logger.failure(`Failed to fetch user, auth token is likely expired.`);
                    Logger.info(`Token: ${this.apiConfig.accessToken}`);
                    Logger.info(`API: ${this.apiConfig.basePath}`);
                    Logger.error('Error Details', e);

                    this.localStorage.remove('user');
                    this.cookies.remove(environment.auth.cookie);
                    this.cookies.remove(environment.auth.userCookie);

                    if (this.user) {
                        this.router.navigate(['login']);
                    }

                    this.user = null;
                    this.apiConfig.accessToken = null;
                    this.markReady();
                })
            ;
        } else {
            this.markReady();
        }

        this.initPrivacyPolicy();
    }

    async initPrivacyPolicy() {
        await this.appConfig.appStatusReady;

        let lastUpdate = this.appConfig.appStatus.settings['privacy_policy_last_updated'];
        let thresholdStr = this.appConfig.appStatus.settings['privacy_policy_threshold_time_days'];
        let thresholdDays = parseInt(thresholdStr || 7);
        let lastUpdateDate : Date;

        if (lastUpdate)
            lastUpdateDate = new Date(lastUpdate);
        else
            lastUpdateDate = new Date('2020-01-01');

        let updateExpiredDate = new Date(lastUpdateDate.getTime() + thresholdDays*1000*60*60*24);
        let shouldShow = false;

        if (Date.now() < updateExpiredDate.getTime()) {
            shouldShow = true;

            let dismissedDateStr = this.cookies.get<string>('_privacyPolicyDismissed');
            let dismissedDate = new Date(dismissedDateStr);

            if (dismissedDate.getTime() > lastUpdateDate.getTime()) {
                shouldShow = false;
            }

            if (shouldShow) {
                this.shell.addAlert({
                    message: "We've updated our privacy policy. Click for details",
                    id: 'privacy-policy',
                    type: 'info',
                    url: `${environment.urls.accounts}/privacy`,
                    onDismiss: () => {
                        this.cookies.set('_privacyPolicyDismissed', new Date().toUTCString());
                    }
                });
            }
        }

    }


    resetSource() {
        this.localStorage.remove('trafficSource');
        this.localStorage.remove('trafficSourceFirstTime');
        this.source = null;
    }

    saveAuthentication(user : User, token : string) {
        this.apiConfig.accessToken = token;
        this.localStorage.set('user', user);
        this.cookies.set(environment.auth.cookie, { token });
        this.cookies.set(environment.auth.userCookie, user.uuid, null, true);
        this.user = user;
    }

    completeLogin(user : User, token : string, hideDialogs = true, defaultAction = null) {
        this.saveAuthentication(user, token);

        if (hideDialogs)
            this.shell.hideDialog();

        setTimeout(() => {
            if (this._postLoginAction) {
                this._postLoginAction();
            } else if (this.postLoginUrl) {
                let destination = this.postLoginUrl;
                this.postLoginUrl = null;
                this.router.navigateByUrl(destination);
            } else if (defaultAction) {
                defaultAction();
            } else {
                this.router.navigate(['login/return']);
            }
        }, 100);
    }

    private justLoggedOut : boolean = false;

    completeLogout() {
        this.justLoggedOut = true;
        this.apiConfig.accessToken = null;

        this.localStorage.remove('user');
        this.cookies.remove(environment.auth.cookie);
        this.cookies.remove(environment.auth.userCookie);

        this.user = null;
    }

    async assume(token) {
        if (!token)
            throw new Error("Cannot assume a user without an access token.");

        this.logger.info(`Assuming user with token ${token}`);

        let originalToken = null;

        if (this.token)
            originalToken = this.token;

        // Store the previous authentication details 

        this.apiConfig.accessToken = token;

        let user : User;
        this.logger.info(`Fetching the assumed user from API...`);
        user = <any>await this.usersApi.getCurrentUser().toPromise();
        
        this.logger.info(`User assumption: Success: Now logged in as ${user.email} (${user.uuid})`);

        user.assumed = true;
        this.completeLogin(user, token, false, () => {});
        this.localStorage.set('originalToken', originalToken);
    }

    get source() {
        if (!this._source) {
            if (this.localStorage.get('trafficSource')) {
                this._source = <string>this.localStorage.get('trafficSource');
            }
        }

        return this._source;
    }

    get recentSource() {
        let time = this.sourceStamp || new Date();
        if (time.getTime() + 1000*60*60*3 < Date.now()) // is expired?
            return null;

        return this.source;
    }

    set source(value : string) {
        console.log(`Setting attribution source to '${value}'`);
        this._source = value;
        this._sourceStamp = new Date();

        this.localStorage.set('trafficSource', value);
        this.localStorage.set('trafficSourceFirstTime', this.firstTime);
        this.localStorage.set('trafficSourceStamp', this._sourceStamp.toISOString());

        this.sourceChanged.next(value);
    }

    get loggedIn() {
        return this.user != null && this.apiConfig.accessToken != null;
    }

    set user(user : User) {
        this._user = user;

        this.applyBillingAlerts(user);
        this.locallyUpdateUser(user);
        if (isPlatformServer(this.platformId))
            this.stateTransfer.set<User>(USER_KEY, user);
    }

    private async applyBillingAlerts(user : User) {
        this.shell.removeAlert('billing');
        this.shell.removeAlert('ytcm299');

        if (user && !user.staff) {
            if (user.membership) {
                let skip_gateways = ['paypal_subscription', 'youtube'];
                if (!skip_gateways.includes(user.membership.gateway_name)) {

                    if (user.membership.cancelled_at) {
                        this.shell.addAlert({
                            id: 'billing',
                            type: 'warning',
                            message: user.membership.status.canceled 
                                ? 'Renew TYT' 
                                : 'Reactivate membership!',
                            url: `/settings/memberships`
                        })  
                    } else if (user.membership.payment_status == 'past_due') {
                        this.shell.addAlert({
                            id: 'billing',
                            type: 'warning',
                            message: `Please update your billing info`,
                            url: `${environment.urls.accounts}/updateBilling`
                        })
                    } else if (!user.membership.entitled) {
                        this.shell.addAlert({
                            id: 'billing',
                            type: 'warning',
                            message: `Renew TYT`,
                            url: `/settings/memberships`
                        });
                    }
                }
            }

            if (!user.entitled && await this.shell.hasFeature('apps.web.enable_ytcm299_messaging')) {
                try {
                    let entitledYT = user.omniauths.find(x => x.provider === 'youtube' && !!x.entitled_sku);
                    let unentitledYT = user.omniauths.find(x => x.provider === 'youtube' && !x.entitled_sku);

                    let rejectGenericSku = await this.shell.hasFeature('apps.web.enable_ytcm299.reject_generic_sku');
                    if (rejectGenericSku) {
                        entitledYT = user.omniauths.find(x => x.provider === 'youtube' && !!x.entitled_sku && x.entitled_sku !== 'youtube_channel_membership');
                        unentitledYT = user.omniauths.find(x => x.provider === 'youtube' && (!x.entitled_sku || x.entitled_sku === 'youtube_channel_membership'));
                    }

                    if (!entitledYT && unentitledYT) {
                        this.shell.addAlert({
                            id: 'ytcm299',
                            type: 'info',
                            internal: false,
                            message: `Support TYT at $4.99/month or more and get unlimited access to all shows. Upgrade your support today.`,
                            url: `https://www.youtube.com/c/TheYoungTurks/membership`
                        });
                    }
                } catch (e) {
                    this.logger.error(`Caught error while checking for YTCM299 condition:`);
                    this.logger.error(e);
                }
            }
        }
    }

    /**
     * Apply a new user object to the entire application.
     * This does not change login state, you must only 
     * pass a modified version of the LOGGED IN USER here
     * or else bad things will happen!
     */
    locallyUpdateUser(user : User) {
        this._user = user;
        
        // Log out the change 

        if (user) {
            this.logger.info(`User ${user.email} [${user.uuid}] is signed in.`);
        } else {
            this.logger.info(`User is now signed out.`);
        }

        // Let Bugsnag know about the user change 

        if (user) {
            Bugsnag.setUser(
                user.uuid,
                user.email,
                user.display_name
            );
        } else {
            Bugsnag.setUser(
                'logged-out',
                'logged-out@tyt.com',
                'Logged Out'
            );
        }

        // Notify all subscriptions
        this.userChanged.next(this._user);
        
    }

    get isLoggedIn() {
        return this._user != null;
    }

    get user() {
        return this._user;
    }

    get entitled() {
        return this.user && this.user.entitled;
    }

    get audienceType() {

        if (!this.user)
            return 'visitor';
        
        if (this.user.entitled)
            return 'member';

        if (this.user.membership)
            return 'expired member';

        return 'registered';
    }
    
    get beaconsEnabled() {
        return this.appConfig.environment === 'production' || window['force_beacons'] === 1;
    }

    /**
     * Send event beacons to Google Analytics and Facebook Pixel because a 
     * new user registration has just occurred (and said user is now logged in)
     */
    async sendRegisteredBeacons() {
        await this.sendBeacons('Social Signup', () => {
            // Standard GA custom event
            this.sendBeaconGA(
                'registrations', 
                [
                    `tytcom_free_registered`,
                    'tytcom_registered',
                    'tyt_registered'
                ], 
                `Registered via Free Signup on TYT.com`
            );
            this.sendBeaconFB('CompleteRegistration', 'tytcom_registered', { 
                registration_type: 'tyt_account' 
            });
        });
    }

    async sendSocialSignupAnalytics(method : 'free' | 'login') {
        await this.sendBeacons(`Social Signup [${method}]`, () => {
            // Google Analytics
            this.sendBeaconGA(
                'registrations', 
                [
                    `tytcom_${method}_social_registered`,
                    'tytcom_registered',
                    'tyt_registered'
                ], 
                `Registered via Social [${'free' ? 'Free Signup' : 'Login'}] on TYT.com`
            );
            this.sendBeaconFB('CompleteRegistration', `tytcom_${method}_social_registered`, { 
                registration_type: 'tyt_account',
                social_method: method
            });
        });
    }

    async sendBeacons(label : string, callback : Function) {
        if (typeof window === 'undefined') {
            console.error(`Fatal Error: App requested beacons be sent for '${label}' on the server side! Ensure that beacons are only sent client side! These beacons will be skipped!`);
            return;
        }

        let beaconsEnabled = this.appConfig.environment === 'production' || window['force_beacons'] === 1;
        if (!beaconsEnabled) {
            this.logger.info(`Analytics: ${label}: Beacons skipped (preproduction)`);
            return;
        }

        this.logger.info(`Analytics: ${label}: Sending beacons...`);
        try {
            await callback();
        } catch (e) {
            this.logger.error(`Caught error while trying to send beacons for event: ${label}`);
            this.logger.error(e);
            this.logger.error(`These beacons will not be retried.`);
        }
        
        // Make sure enough time is allowed for the beacons to go out.
    
        this.logger.info(`Allowing signup event beacons to complete...`);
        await this.timeout(3000);
        this.logger.info(`Beacon sending completed.`);
    }

    sendBeaconFB(eventName : string, contentName : string, metadata : Record<string, any> = {}, isCustom = false) {
        this.logger.info(`FB: Beacon ${eventName}/${contentName}`);

        if (typeof window['xfbq'] === 'undefined')
            return;
        
        let xfbq = window['xfbq'];
        xfbq(
            isCustom ? 'trackCustom' : 'track', 
            eventName,
            Object.assign({
                content_name: contentName,
                user_uuid: this.user ? this.user.uuid : null,
            }, metadata)
        );
    }

    sendBeaconGA(category : string, eventNames : string[], label : string, value : number = 0, metadata : Record<string,any> = {}) {
        let gaProps = { category, label, value };
        let appWebProps = {
            event: '',
            userId: this.user ? this.user.uuid : undefined,
            value,
            label,
            gtmCustom: {}
        };

        Object.assign(gaProps, metadata);
        Object.assign(appWebProps.gtmCustom, metadata);

        // Send the actual events 

        for (let name of eventNames) {
            this.logger.info(`GA: Beacon ${category}/${name}/${value} '${label}'`);
            
            let ga;
            if (window['ga'])
                ga = window['ga'];

            // FOR TESTING DURING DEVELOPMENT:
            // - To test in development, send direct to GA. On staging and production events get to GA via GTM, 
            // see "Custom Event" tags in our GTM property.
            // if (!environment.production)
            //     ga('send', 'event', category, name, label, metadata);

            // Notify GTM of the event (see Custom Event triggers in GTM)
            this.angulartics2.eventTrack.next({ action: name, properties: gaProps });
            this.angulartics2GoogleTagManager.eventTrack(name, Object.assign({}, appWebProps, { event: name })); // possibly unneeded

            // Notify GA4 directly of the event, because GTM would require us to set up a trigger and tag for every 
            // individual event in order to send custom parameters.

            if (environment.production)
                gtag('event', name, metadata);

            this.shell.sendSyntheticPageView(`/ga/event-pages/${name.replace(/_/g, '-')}${metadata.spvSlug ? `/${metadata.spvSlug}` : ``}`);
        }
    }

    private sendAppWebEvent(event : string, props : Record<string,any>) {
      this.angulartics2GoogleTagManager.eventTrack(
        event, 
        Object.assign({}, props, { event })
      );
    }

    private timeout(time = 0) {
      return new Promise<void>(resolve => setTimeout(() => resolve(), time));
    }
}